import React, { useEffect, useState } from 'react';
import { Activity, RefreshCw, Plus, X, ChevronDown, ChevronRight, Clock, Search, Shield, User } from 'lucide-react';
import { useDiagnosticsStore } from '@/stores/agents/diagnostics-store';
import { useAuthStore } from '@/stores/auth/auth-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import type { DiagnosticTraceEntry, DiagnosticTraceSummary } from '@/services/api/agentApi';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 60000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
  return `${Math.round(diff / 86400000)}d ago`;
}

function formatDuration(ms: number | undefined): string {
  if (ms === undefined || ms === null) return '--';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function traceStatus(entries: DiagnosticTraceEntry[]): 'ok' | 'error' | 'warning' {
  if (entries.some((e) => e.status === 'error' || e.status === 'timeout')) return 'error';
  if (entries.some((e) => e.status === 'warning')) return 'warning';
  return 'ok';
}

const statusDotColor: Record<string, string> = {
  ok: 'bg-emerald-500',
  error: 'bg-red-500',
  warning: 'bg-amber-500',
};

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return 'null';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }
  const s = String(v);
  if (s.length > 200) return s.slice(0, 200) + '...';
  return s;
}

function isJsonLike(v: unknown): boolean {
  return typeof v === 'object' && v !== null;
}

// ---------------------------------------------------------------------------
// TTL options
// ---------------------------------------------------------------------------

const TTL_OPTIONS = [
  { label: '1 hour', value: 1 },
  { label: '6 hours', value: 6 },
  { label: '24 hours', value: 24 },
  { label: '7 days', value: 168 },
];

// ---------------------------------------------------------------------------
// TraceEntryCard
// ---------------------------------------------------------------------------

const borderColorMap: Record<string, string> = {
  ok: 'border-l-emerald-500',
  error: 'border-l-red-500',
  warning: 'border-l-amber-500',
  timeout: 'border-l-red-500',
};

const badgeColorMap: Record<string, string> = {
  ok: 'bg-emerald-500/10 text-emerald-600',
  error: 'bg-red-500/10 text-red-600',
  warning: 'bg-amber-500/10 text-amber-600',
  timeout: 'bg-red-500/10 text-red-600',
};

const TraceEntryCard: React.FC<{
  entry: DiagnosticTraceEntry;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ entry, isSelected, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const borderColor = borderColorMap[entry.status] || 'border-l-gray-400';
  const badgeColor = badgeColorMap[entry.status] || 'bg-gray-500/10 text-gray-600';

  const dataEntries = Object.entries(entry.data || {});

  return (
    <div
      className={cn(
        'border-l-4 rounded-lg bg-card border border-border p-3 cursor-pointer transition-colors',
        borderColor,
        isSelected && 'ring-2 ring-primary/50',
      )}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={cn('text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded', badgeColor)}>
            {entry.status}
          </span>
          <span className="text-sm font-medium truncate">{entry.step}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-muted-foreground">@ {formatDuration(entry.elapsed_ms)}</span>
          {dataEntries.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(!expanded);
              }}
              className="p-0.5 hover:bg-muted rounded"
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>

      {expanded && dataEntries.length > 0 && (
        <div className="mt-2 pt-2 border-t border-border space-y-1">
          {dataEntries.map(([key, value]) => (
            <div key={key} className="flex gap-2 text-xs">
              <span className="text-muted-foreground font-medium flex-shrink-0">{key}:</span>
              {isJsonLike(value) ? (
                <pre className="font-mono text-foreground whitespace-pre-wrap break-all bg-muted/50 rounded px-1.5 py-0.5 max-h-48 overflow-auto flex-1">
                  {formatValue(value)}
                </pre>
              ) : (
                <span className="font-mono text-foreground break-all">{formatValue(value)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DiagnosticsTab
// ---------------------------------------------------------------------------

export const DiagnosticsTab: React.FC = () => {
  const {
    tracedUsers,
    traces,
    total,
    selectedTrace,
    selectedEntry,
    filters,
    page,
    pageSize,
    isLoading,
    error,
    fetchStatus,
    enableTracing,
    disableTracing,
    fetchTraces,
    selectTrace,
    selectEntry,
    setFilter,
    clearFilters,
    setPage,
  } = useDiagnosticsStore();

  // Current user info
  const currentUser = useAuthStore((s) => s.user);
  const currentContext = useOrganizationStore((s) => s.currentContext);
  const currentUserId = currentUser?.id || '';
  const currentUserEmail = currentUser?.email || '';
  const currentOrgId = currentContext?.id || '';

  // Enable form local state
  const [enableTtl, setEnableTtl] = useState(1);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advancedUserId, setAdvancedUserId] = useState('');
  const [advancedOrgId, setAdvancedOrgId] = useState('');
  const [isEnabling, setIsEnabling] = useState(false);

  // Filter local state (debounced push)
  const [filterUserId, setFilterUserId] = useState('');
  const [filterOrgId, setFilterOrgId] = useState('');

  // Check if current user is already being traced
  const isCurrentUserTraced = tracedUsers.some(
    (u) => u.userId === currentUserId && u.orgId === currentOrgId,
  );

  useEffect(() => {
    fetchStatus();
    fetchTraces();
  }, [fetchStatus, fetchTraces]);

  // Re-fetch traces when filters / page change
  useEffect(() => {
    fetchTraces();
  }, [fetchTraces, filters, page]);

  const handleTraceMe = async () => {
    if (!currentUserId || !currentOrgId) return;
    setIsEnabling(true);
    await enableTracing(currentUserId, currentOrgId, enableTtl);
    setIsEnabling(false);
  };

  const handleEnableAdvanced = () => {
    if (!advancedUserId.trim()) return;
    enableTracing(advancedUserId.trim(), advancedOrgId.trim() || currentOrgId, enableTtl);
    setAdvancedUserId('');
    setAdvancedOrgId('');
  };

  const handleApplyFilters = () => {
    setFilter('userId', filterUserId.trim());
    setFilter('orgId', filterOrgId.trim());
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Section A: Controls bar */}
      <div className="flex-shrink-0 p-4 border-b border-border bg-card space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">Diagnostic Tracing</span>
        </div>

        {/* Quick enable: Trace Me */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleTraceMe}
            disabled={!currentUserId || !currentOrgId || isCurrentUserTraced || isEnabling}
            className={cn(
              'h-8 px-4 text-sm rounded-md flex items-center gap-1.5 font-medium transition-colors',
              isCurrentUserTraced
                ? 'bg-emerald-500/10 text-emerald-600 cursor-default'
                : 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {isEnabling ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <User className="h-3.5 w-3.5" />
            )}
            {isEnabling ? 'Enabling...' : isCurrentUserTraced ? 'Tracing active' : 'Trace Me'}
          </button>
          <span className="text-xs text-muted-foreground">
            {currentUserEmail || currentUserId}
          </span>
          <span className="text-xs text-muted-foreground/60">for</span>
          <select
            value={enableTtl}
            onChange={(e) => setEnableTtl(Number(e.target.value))}
            className="h-8 px-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {TTL_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <div className="ml-auto">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {showAdvanced ? 'Hide' : 'Other user...'}
            </button>
          </div>
        </div>

        {/* Advanced: trace another user by ID */}
        {showAdvanced && (
          <div className="flex items-center gap-2 flex-wrap pl-4 border-l-2 border-border">
            <input
              type="text"
              placeholder="User ID"
              value={advancedUserId}
              onChange={(e) => setAdvancedUserId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEnableAdvanced()}
              className="h-7 px-2 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary w-48"
            />
            <input
              type="text"
              placeholder="Org ID (optional)"
              value={advancedOrgId}
              onChange={(e) => setAdvancedOrgId(e.target.value)}
              className="h-7 px-2 text-xs rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary w-48"
            />
            <button
              onClick={handleEnableAdvanced}
              disabled={!advancedUserId.trim()}
              className="h-7 px-2.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Enable
            </button>
          </div>
        )}

        {/* Traced users badges */}
        {tracedUsers.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-muted-foreground mr-1">Active:</span>
            {tracedUsers.map((u) => {
              const isMe = u.userId === currentUserId;
              const displayName = isMe ? (currentUserEmail || 'Me') : u.userId.substring(0, 8) + '...';
              const ttlDisplay = u.ttl > 3600 ? `${Math.round(u.ttl / 3600)}h left` : `${Math.round(u.ttl / 60)}m left`;
              return (
                <span
                  key={`${u.orgId}-${u.userId}`}
                  className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full"
                >
                  <Activity className="h-3 w-3" />
                  {displayName}
                  <span className="text-muted-foreground">({ttlDisplay})</span>
                  <button
                    onClick={() => disableTracing(u.userId, u.orgId)}
                    className="ml-0.5 hover:bg-destructive/20 rounded-full p-0.5"
                    title="Stop tracing"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {error && (
          <div className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1">{error}</div>
        )}
      </div>

      {/* Main content */}
      <div className="flex-1 min-h-0 flex">
        {/* Section B: Left panel — trace list */}
        <div className="w-80 border-r border-border flex flex-col bg-card/50">
          {/* Filters */}
          <div className="p-3 border-b border-border space-y-2">
            <div className="flex items-center gap-1.5">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Filter by userId"
                  value={filterUserId}
                  onChange={(e) => setFilterUserId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  className="h-7 w-full pl-7 pr-2 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <button
                onClick={() => {
                  handleApplyFilters();
                  fetchTraces();
                }}
                className="h-7 px-2 rounded border border-border bg-background hover:bg-muted"
                title="Refresh"
              >
                <RefreshCw className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')} />
              </button>
            </div>
            <input
              type="text"
              placeholder="Filter by orgId"
              value={filterOrgId}
              onChange={(e) => setFilterOrgId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              className="h-7 w-full px-2 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {(filters.userId || filters.orgId) && (
              <button
                onClick={() => {
                  clearFilters();
                  setFilterUserId('');
                  setFilterOrgId('');
                }}
                className="text-[10px] text-muted-foreground hover:text-foreground"
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Trace list */}
          <div className="flex-1 overflow-y-auto">
            {traces.length === 0 && !isLoading && (
              <div className="p-6 text-center text-xs text-muted-foreground">No traces found</div>
            )}
            {isLoading && traces.length === 0 && (
              <div className="p-6 text-center text-xs text-muted-foreground">Loading...</div>
            )}
            {traces.map((trace) => {
              const status = traceStatus(trace.entries);
              const isActive = selectedTrace?.queryTraceId === trace.queryTraceId;
              return (
                <button
                  key={trace.queryTraceId}
                  onClick={() => selectTrace(trace)}
                  className={cn(
                    'w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted/50 transition-colors',
                    isActive && 'bg-muted',
                  )}
                >
                  <div className="flex items-start gap-2">
                    <span className={cn('mt-1.5 h-2 w-2 rounded-full flex-shrink-0', statusDotColor[status])} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate leading-snug">
                        {trace.userMessage
                          ? trace.userMessage.length > 80
                            ? trace.userMessage.slice(0, 80) + '...'
                            : trace.userMessage
                          : '(no message)'}
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-0.5">
                          <Clock className="h-3 w-3" />
                          {timeAgo(trace.started_at || trace.created_at)}
                        </span>
                        <span>{formatDuration(trace.total_duration_ms)}</span>
                        <span>{trace.entries.length} steps</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex-shrink-0 p-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
              <button
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
                className="px-2 py-1 rounded hover:bg-muted disabled:opacity-30"
              >
                Prev
              </button>
              <span>
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
                className="px-2 py-1 rounded hover:bg-muted disabled:opacity-30"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Section C: Right panel — trace detail */}
        <div className="flex-1 overflow-y-auto p-4">
          {!selectedTrace ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Select a trace to view details
            </div>
          ) : (
            <div className="space-y-4">
              {/* Header */}
              <div className="space-y-1">
                <h2 className="text-base font-semibold leading-snug">{selectedTrace.userMessage || '(no message)'}</h2>
                <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                  <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-[10px]">
                    {selectedTrace.queryTraceId}
                  </span>
                  <span>User: {selectedTrace.userId}</span>
                  {selectedTrace.total_duration_ms !== undefined && (
                    <span>Total: {formatDuration(selectedTrace.total_duration_ms)}</span>
                  )}
                  <span>{selectedTrace.entries.length} steps</span>
                </div>
              </div>

              {/* Timeline */}
              <div className="space-y-2">
                {selectedTrace.entries.map((entry, idx) => (
                  <TraceEntryCard
                    key={`${entry.step}-${idx}`}
                    entry={entry}
                    isSelected={selectedEntry?.step === entry.step && selectedEntry?.timestamp === entry.timestamp}
                    onSelect={() => selectEntry(entry)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticsTab;
